// src/pages/TrafosEditor.jsx
// --- Repeater fuer Transformatoren -------------------------------------------
import { C, inputStyle, Field, TextInput, NumberInput, ComboInput, Select, Checkbox, TriToggle, Row, Subsection, PlausiHinweis, plausi } from './formui';
import { TRAFO_HERSTELLER, TRAFO_LEISTUNG_KVA, OS_SPANNUNG_KV, US_SPANNUNG_KV, SCHALTGRUPPEN } from '../lib/auswahllisten';
import { TRAFO_TYPEN, typenFuerTrafoHersteller } from '../lib/typenlisten';
import NSKabelBlock from './NSKabelBlock';
import OelWanneBlock from './OelWanneBlock';
import { t } from '../lib/sprache';

const TRAFOART = [
  { value: 'hermetic', label: 'Hermetic (Oel, geschlossen)' },
  { value: 'giessharz', label: 'Giessharz (trocken)' },
  { value: 'oel_mit_ausdehner', label: 'Oel mit Ausdehner' },
];
// Anschlussarten je Trafo-Art (Anna-Lena nach dem Testlauf 13.09.2026: "bei
// GH nur die moeglichen fuer GH, bei Oel nur die fuer Oel").
//   Oel-Trafo:  OS ueber Porzellan-Durchfuehrungen ("Kerzen") oder
//               Steckdurchfuehrungen (Aussenkonus); US ueber Durchfuehrungen
//               offen, mit Klemmen/Flachanschluss oder mit Schutzhauben.
//   GH-Trafo:   keine Durchfuehrungen - OS an Anschlussfahnen/-bolzen der
//               Wicklung oder Steckanschluss; US an Anschlussschienen.
// US-Seite seit 18.09.2026 (Anna-Lena, Migration 48): Oel-Trafo Kerzen mit
// Flachanschluss oder Anschlussfahne, Klemmen ohne Haube, Klemmen mit Hauben
// mit oder ohne Erdungskugelpunkte. GH-Trafo: nur Anschlussfahnen - wird
// automatisch eingetragen. Seit Migration 53 (18.09.2026) jede Oel-Variante
// "mit / ohne Erdungsbolzen"; "ohne" setzt 04.3 automatisch auf n.i.O.
// Alte Werte (ohne Angabe zum Erdungsbolzen) bleiben lesbar.
const ANSCHLUSS_TEXT = {
  kerzen: 'Porzellan-Durchführungen (Kerzen)',
  stecker: 'Steckanschluss (Außenkonus)',
  anschlussfahnen: 'Anschlussfahnen / -bolzen',
  kerzen_flach_eb: 'Porzellan-Kerzen mit Flachanschluss, mit Erdungsbolzen',
  kerzen_flach_ohne_eb: 'Porzellan-Kerzen mit Flachanschluss, ohne Erdungsbolzen',
  kerzen_fahne_eb: 'Porzellan-Kerzen mit Anschlussfahne, mit Erdungsbolzen',
  kerzen_fahne_ohne_eb: 'Porzellan-Kerzen mit Anschlussfahne, ohne Erdungsbolzen',
  klemmen_eb: 'Klemmen ohne Haube, mit Erdungsbolzen',
  klemmen_ohne_eb: 'Klemmen ohne Haube, ohne Erdungsbolzen',
  klemmen_hauben_kugel: 'Klemmen mit Hauben, mit Erdungsbolzen',
  klemmen_hauben_ohne_kugel: 'Klemmen mit Hauben, ohne Erdungsbolzen',
  // alte Werte
  kerzen_flach: 'Porzellan-Kerzen mit Flachanschluss',
  kerzen_fahne: 'Porzellan-Kerzen mit Anschlussfahne',
  klemmen: 'Klemmen ohne Haube',
  klemmen_und_hauben: 'Klemmen mit Schutzhauben',
  anschlussschienen: 'Anschlussschienen',
  anschlussschienen_abgedeckt: 'Anschlussschienen mit Abdeckung',
};
const OS_JE_ART = { oel: ['kerzen', 'stecker'], gh: ['anschlussfahnen', 'stecker'] };
const US_JE_ART = {
  oel: ['kerzen_flach_eb', 'kerzen_flach_ohne_eb', 'kerzen_fahne_eb', 'kerzen_fahne_ohne_eb',
        'klemmen_eb', 'klemmen_ohne_eb', 'klemmen_hauben_kugel', 'klemmen_hauben_ohne_kugel'],
  gh: ['anschlussfahnen'],
};
// Wechsel auf Giessharz: US-Anschluss gibt es dort nur als Anschlussfahnen -
// gleich mit ausfuellen (Anna-Lena: "kann man automatisch ausfuellen").
// Waehlt jemand den US-Anschluss selbst, bleibt seine Wahl stehen.
// (Der Anschluss am NS-Kabel ist seit 18.09.2026 raus - er war doppelt.)
function automatikNachAenderung(tr, feld) {
  if (feld === 'trafoart' && tr.trafoart === 'giessharz' && tr.us_anschluss !== 'anschlussfahnen') {
    return { ...tr, us_anschluss: 'anschlussfahnen' };
  }
  return tr;
}

// Auswahlliste passend zur Trafo-Art. Ohne Trafo-Art: alle. Ein bereits
// gespeicherter Wert, der nicht passt, bleibt sichtbar (mit Hinweis) - nichts
// wird stillschweigend geloescht.
function anschlussOptionen(jeArt, trafoart, aktuell) {
  const art = trafoart === 'giessharz' ? 'gh' : trafoart ? 'oel' : null;
  const werte = art ? jeArt[art] : [...new Set([...jeArt.oel, ...jeArt.gh])];
  const liste = werte.map(v => ({ value: v, label: t(ANSCHLUSS_TEXT[v]) }));
  if (aktuell && !werte.includes(aktuell)) {
    // Alte Oel-Werte ohne Angabe zum Erdungsbolzen: nur die Bezeichnung, ohne
    // Zusatz (Anna-Lena 18.09.2026: "bitte genauer waehlen" weg).
    const altOel = ['kerzen', 'kerzen_flach', 'kerzen_fahne', 'klemmen', 'klemmen_und_hauben'].includes(aktuell) && trafoart !== 'giessharz';
    liste.unshift({ value: aktuell, label: (ANSCHLUSS_TEXT[aktuell] ? t(ANSCHLUSS_TEXT[aktuell]) : aktuell)
      + (altOel ? '' : ' (' + t('passt nicht zur Trafo-Art') + ')') });
  }
  return liste;
}
const MITNAHME = [
  { value: 'ok', label: 'In Ordnung' },
  { value: 'defekt', label: 'Funktioniert nicht' },
  { value: 'nicht_benoetigt', label: 'Nicht benoetigt (Stand-alone)' },
];

export const DEFAULT_SCHUTZ = {
  vollschutz: { vorhanden: false, typ: '' },
  buchholz: { vorhanden: false, typ: '' },
  thermometer: { vorhanden: false, typ: '' },
  kaltleiter: false,
  kein_schutz: false,
  schutz_am_trafo: null,
  schutz_an_ms_anlage: null,
  ausloesespule_ms_anlage: null,
  mitnahmeschaltung: null,
};

// Schutzfunktionen gibt es in ZWEI Formen: aus dem Import flach
// (buchholz: true, schutz_am_trafo: true) und aus diesem Editor als Objekt
// (buchholz: { vorhanden, typ }, schutz_am_trafo: 'a'/'b'). Beim Lesen auf die
// Editor-Form bringen - vorher erschienen die importierten Haken leer.
export function normSchutz(s) {
  const q = s || {};
  const n = (v) => (v && typeof v === 'object')
    ? { vorhanden: !!v.vorhanden, typ: v.typ || '' }
    : { vorhanden: v === true, typ: '' };
  const tri = (v) => (v === true ? 'a' : v === false ? 'b' : (v ?? null));
  return {
    ...DEFAULT_SCHUTZ, ...q,
    vollschutz: n(q.vollschutz), buchholz: n(q.buchholz), thermometer: n(q.thermometer),
    kaltleiter: !!q.kaltleiter, kein_schutz: !!q.kein_schutz,
    schutz_am_trafo: tri(q.schutz_am_trafo),
    schutz_an_ms_anlage: tri(q.schutz_an_ms_anlage),
    ausloesespule_ms_anlage: tri(q.ausloesespule_ms_anlage),
  };
}

export const DEFAULT_TRAFO = {
  bezeichnung: '', hersteller: '', typ: '', seriennummer: '', herstellungsjahr: null,
  leistung_kva: null, os_spannung_v: null, us_spannung_v: null,
  uk_prozent: null, schaltstufen: '', position_schaltstufe: '',
  schaltgruppe: '', os_anzapfungen_prozent: null, os_spannungen_je_stufe: [],
  gesamt_gewicht_kg: null, oel_gewicht_kg: null,
  trafoart: null, os_anschluss: null, us_anschluss: null,
  schutzfunktionen: { ...DEFAULT_SCHUTZ },
  sonstiges: '', notizen: '',
  // NS-Kabelverbindung + Oelwanne pro Trafo
  ns_kabel_aussenleiter: {}, ns_kabel_neutral: {},
  oel_wanne_vorhanden: false, oel_wanne_laenge_m: null,
  oel_wanne_breite_m: null, oel_wanne_hoehe_cm: null,
  trafo_in_oel_wanne: false, oel_wanne_notiz: '',
};

// Berechnet die OS-Anzapfung in % aus den Stufenwerten.
// Formel (mit Anna-Lena 14.05. abgestimmt):
//   Nennspannung = mittlere Stufe (Median bei ungerader Anzahl)
//   Stufenspannung = (Max - Min) / (Anzahl - 1)
//   OS-Anzapfung (%) = Stufenspannung / Nennspannung * 100
// Beispiel: [19, 19.5, 20, 20.5, 21] kV -> 0.5 / 20 * 100 = 2.5 %
// Seit v63 nur, wenn ALLE Stufen eingetragen sind: Mit Luecken (Stufe 1 und 5
// von 5) teilte die Rechnung durch die Zahl der gefuellten Stufen - 10 % statt
// 2,5 %. Und waehrend des Tippens ("1" von "19,5") entstanden Unsinnswerte.
function calculateOsAnzapfung(stufen) {
  if (!Array.isArray(stufen) || stufen.length < 2) return null;
  const valid = stufen.filter(s => s != null && !isNaN(s) && s > 0);
  if (valid.length !== stufen.length) return null;
  const sorted = [...valid].sort((a, b) => a - b);
  const min = sorted[0];
  const max = sorted[sorted.length - 1];
  const median = sorted[Math.floor(sorted.length / 2)];
  if (median === 0) return null;
  const stufenspannung = (max - min) / (sorted.length - 1);
  const prozent = (stufenspannung / median) * 100;
  // Mehr als 10 % je Stufe gibt es nicht - dann ist noch ein Wert unfertig.
  if (!isFinite(prozent) || prozent > 10) return null;
  return Math.round(prozent * 100) / 100; // auf 2 Nachkommastellen runden
}

// einzeln = true: EIN Trafo im Wartungsprotokoll. Dann gibt es kein
// "hinzufuegen" und kein "entfernen" (dafuer ist "+ Trafo fehlt?" unter dem
// Kapitel da), und die Oelwanne steht nicht hier, sondern im eigenen Kapitel.
// Sonst ist es exakt derselbe Editor wie in den Stammdaten - Anna-Lena:
// "ich brauche bei der Wartung alle Stammdatenfelder des Trafos". Ein
// gemeinsamer Baustein heisst: beide Stellen koennen nie auseinanderlaufen.
export default function TrafosEditor({ items, setItems, einzeln = false }) {
  const add = () => setItems([...items, { ...DEFAULT_TRAFO, bezeichnung: 'Trafo ' + (items.length + 1), schutzfunktionen: { ...DEFAULT_SCHUTZ } }]);
  const remove = (i) => setItems(items.filter((_, idx) => idx !== i));
  const update = (i, key, val) => setItems(items.map((it, idx) => {
    if (idx !== i) return it;
    const neu = { ...it, [key]: val };
    return key === 'trafoart' || key === 'us_anschluss' ? automatikNachAenderung(neu, key) : neu;
  }));

  const updateSchaltstufen = (i, val) => {
    const n = parseInt(val) || 0;
    const cur = items[i].os_spannungen_je_stufe || [];
    let arr;
    if (n === 0) arr = [];
    else if (n > cur.length) arr = [...cur, ...Array(n - cur.length).fill(null)];
    else arr = cur.slice(0, n);
    const auto = calculateOsAnzapfung(arr);
    setItems(items.map((it, idx) => idx === i ? {
      ...it,
      schaltstufen: val,
      os_spannungen_je_stufe: arr,
      // Auto-Berechnung nur setzen, wenn Wert errechenbar
      os_anzapfungen_prozent: auto != null ? auto : it.os_anzapfungen_prozent,
    } : it));
  };
  const updateStufe = (i, idx, val) => {
    const arr = [...(items[i].os_spannungen_je_stufe || [])];
    arr[idx] = val === '' ? null : Number(val);
    const auto = calculateOsAnzapfung(arr);
    setItems(items.map((it, k) => k === i ? {
      ...it,
      os_spannungen_je_stufe: arr,
      // Live-Update der Auto-Berechnung
      os_anzapfungen_prozent: auto != null ? auto : it.os_anzapfungen_prozent,
    } : it));
  };

  // Schutz-Mutex-Logik (15.05.): "Kein Schutz vorhanden" ist exklusiv mit allen anderen
  // Schutz-Optionen. Wenn eines aktiviert wird, wird das andere automatisch zurueckgesetzt.
  const hasAnySchutz = (s) => !!(s?.vollschutz?.vorhanden || s?.buchholz?.vorhanden ||
    s?.thermometer?.vorhanden || s?.kaltleiter);

  const updSchutz = (i, key, val) => {
    const cur = normSchutz(items[i].schutzfunktionen);
    let next = { ...cur, [key]: val };
    if (key === 'kein_schutz' && val === true) {
      // 'Kein Schutz' aktiviert -> alle anderen Schutz-Optionen zuruecksetzen.
      next = {
        ...next,
        vollschutz: { vorhanden: false, typ: '' },
        buchholz: { vorhanden: false, typ: '' },
        thermometer: { vorhanden: false, typ: '' },
        kaltleiter: false,
      };
    } else if (key === 'kaltleiter' && val === true) {
      // Kaltleiter aktiviert -> 'Kein Schutz' zuruecksetzen.
      next.kein_schutz = false;
    }
    update(i, 'schutzfunktionen', next);
  };
  const updSchutzN = (i, p, k, v) => {
    const cur = normSchutz(items[i].schutzfunktionen);
    let next = { ...cur, [p]: { ...(cur[p] || {}), [k]: v } };
    // Wenn eine vorhandene-Flag auf true gesetzt wird -> 'Kein Schutz' zuruecksetzen.
    if (k === 'vorhanden' && v === true) {
      next.kein_schutz = false;
    }
    update(i, 'schutzfunktionen', next);
  };

  const isOel = (tr) => tr.trafoart && tr.trafoart !== 'giessharz';
  // Auswahllisten: gespeichert bleibt value, nur die Anzeige wird uebersetzt.
  const mitT = (liste) => liste.map(o => ({ ...o, label: t(o.label) }));

  if (items.length === 0) {
    return (
      <>
        <div style={{ padding: 14, color: C.gray500, fontSize: 15, fontStyle: 'italic', textAlign: 'center' }}>
          {t('Noch kein Transformator. Auf "+ Transformator hinzufuegen" klicken.')}
        </div>
        <button onClick={add} style={{ marginTop: 4, padding: '12px 18px', background: C.navy, color: C.yellow, border: 'none', borderRadius: 8, cursor: 'pointer', fontWeight: 800, fontSize: 14 }}>{t('+ Transformator hinzufuegen')}</button>
      </>
    );
  }

  return (
    <>
      {items.map((trafo, i) => {
        const stufen = trafo.os_spannungen_je_stufe || [];
        const schutz = normSchutz(trafo.schutzfunktionen);
        const otherSchutzActive = hasAnySchutz(schutz);
        const keinSchutzActive = !!schutz.kein_schutz;
        const autoAnzapfung = calculateOsAnzapfung(stufen);
        const istGh = trafo.trafoart === 'giessharz';
        return (
          <div key={i} style={{ padding: 16, marginBottom: 14, background: C.gray100, borderRadius: 10 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 12, paddingBottom: 8, borderBottom: '2px solid ' + C.gray200 }}>
              <div style={{ fontWeight: 800, color: C.navy, fontSize: 16 }}>
                {einzeln ? t('Trafo-Stammdaten: {name}', { name: trafo.bezeichnung || t('Trafo') }) : t('Trafo {nr}', { nr: i + 1 })}
              </div>
              {!einzeln && (
                <button onClick={() => remove(i)} style={{ background: C.redBg, color: C.red, border: 'none', borderRadius: 8, padding: '8px 14px', minHeight: 44, cursor: 'pointer', fontSize: 14, fontWeight: 700 }}>{t('Entfernen')}</button>
              )}
            </div>

            <Subsection title={t('Stammdaten')}>
              <Row columns={2}>
                <Field label={t('Bezeichnung')}><TextInput value={trafo.bezeichnung} onChange={v => update(i, 'bezeichnung', v)}/></Field>
                <Field label={t('Trafo-Art')}><Select value={trafo.trafoart} onChange={v => update(i, 'trafoart', v)} options={mitT(TRAFOART)}/></Field>
              </Row>
              <Row columns={2}>
                <Field label={t('Hersteller')} hint={t('Auswählen oder eigenen eintippen')}><ComboInput value={trafo.hersteller} onChange={v => update(i, 'hersteller', v)} options={[...new Set([...TRAFO_HERSTELLER, ...TRAFO_TYPEN.map(e => e.hersteller)])]} placeholder={t('z. B. SGB')}/></Field>
                {/* Baureihen passend zu Hersteller und Trafo-Art (Oel / Giessharz). */}
                <Field label={t('Typ / Modell')} hint={trafo.hersteller && typenFuerTrafoHersteller(trafo.hersteller).length ? t('Baureihen des Herstellers zur Auswahl') : undefined}>
                  <ComboInput value={trafo.typ} onChange={v => update(i, 'typ', v)}
                    options={typenFuerTrafoHersteller(trafo.hersteller, trafo.trafoart === 'giessharz' ? 'giessharz' : trafo.trafoart ? 'oel' : undefined)}
                    placeholder={t('Typ vom Typenschild')}/>
                </Field>
              </Row>
              <Row columns={2}>
                <Field label={t('Seriennummer')}><TextInput value={trafo.seriennummer} onChange={v => update(i, 'seriennummer', v)}/></Field>
                <Field label={t('Herstellungsjahr')}><NumberInput value={trafo.herstellungsjahr} onChange={v => update(i, 'herstellungsjahr', v)}/><PlausiHinweis text={plausi.herstellungsjahr(trafo.herstellungsjahr)}/></Field>
              </Row>
            </Subsection>

            <Subsection title={t('Elektrische Daten')}>
              <Row columns={3}>
                <Field label={t('Leistung [kVA]')} hint={t('Normreihe, Sonderwerte frei eintippbar')}><ComboInput value={trafo.leistung_kva} onChange={v => update(i, 'leistung_kva', v === '' ? null : v)} options={TRAFO_LEISTUNG_KVA} placeholder={t('z. B. 630')}/><PlausiHinweis text={plausi.leistungKva(trafo.leistung_kva)}/></Field>
                <Field label={t('OS-Spannung [kV]')} hint={t('Betriebsspannung, z. B. 20')}><ComboInput value={trafo.os_spannung_v} onChange={v => update(i, 'os_spannung_v', v === '' ? null : v)} options={OS_SPANNUNG_KV} placeholder={t('z. B. 20')}/><PlausiHinweis text={plausi.spannungKv(trafo.os_spannung_v)}/></Field>
                <Field label={t('US-Spannung [kV]')} hint={t('z. B. 0,4')}><ComboInput value={trafo.us_spannung_v} onChange={v => update(i, 'us_spannung_v', v === '' ? null : v)} options={US_SPANNUNG_KV} placeholder={t('z. B. 0,4')}/></Field>
              </Row>
              <Row columns={2}>
                <Field label='Uk [%]'><NumberInput value={trafo.uk_prozent} onChange={v => update(i, 'uk_prozent', v)} step={0.01}/></Field>
                <Field label={t('Schaltgruppe')} hint={t('Dyn5 und Dyn11 sind die häufigsten')}><ComboInput value={trafo.schaltgruppe} onChange={v => update(i, 'schaltgruppe', v)} options={SCHALTGRUPPEN} placeholder={t('z. B. Dyn5')}/></Field>
              </Row>
            </Subsection>

            <Subsection title={t('Schaltstufen')}>
              <Row columns={2}>
                <Field label={t('Anzahl Schaltstufen')} hint={t('Meist 3 oder 5 – erzeugt die Felder für die Stufenspannungen')}><NumberInput value={trafo.schaltstufen} onChange={v => updateSchaltstufen(i, v === null ? '' : String(v))}/></Field>
                <Field label={t('Aktuelle Position')}><TextInput value={trafo.position_schaltstufe} onChange={v => update(i, 'position_schaltstufe', v)} placeholder={t('z.B. 3')}/></Field>
              </Row>
              {/* Reihenfolge (Anna-Lena 18.09.2026): erst die Stufen ausfuellen,
                  DARUNTER die OS-Anzapfung, die daraus berechnet wird. Umgekehrt
                  verwirrte es. */}
              {stufen.length > 0 && (
                <div style={{ marginTop: 10, padding: 12, background: C.white, borderRadius: 8 }}>
                  <div style={{ fontSize: 14, fontWeight: 700, color: C.gray700, marginBottom: 8 }}>{t('OS-Spannungen je Stufe [kV]')}</div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: 10, alignItems: 'start' }}>
                    {stufen.map((sp, idx) => (
                      <Field key={idx} label={t('Stufe {nr}', { nr: idx + 1 })}>
                        <input type='number' step={0.001} value={sp ?? ''} onChange={e => updateStufe(i, idx, e.target.value)} placeholder='kV' style={inputStyle}/>
                      </Field>
                    ))}
                  </div>
                </div>
              )}
              <Row columns={1}>
                <Field label={t('OS-Anzapfungen [%]')} hint={autoAnzapfung != null ? t('Automatisch aus den Stufen berechnet: {wert} % — kann von Hand überschrieben werden', { wert: autoAnzapfung }) : stufen.length > 0 ? t('Wird berechnet, sobald alle Stufen oben eingetragen sind') : t('Erst die Anzahl der Schaltstufen eintragen, dann rechnet die App selbst')}>
                  <NumberInput value={trafo.os_anzapfungen_prozent} onChange={v => update(i, 'os_anzapfungen_prozent', v)} step={0.5}/>
                </Field>
              </Row>
            </Subsection>

            <Subsection title={t('Gewicht')}>
              <Row columns={isOel(trafo) ? 2 : 1}>
                <Field label={t('Gesamtgewicht [kg]')}><NumberInput value={trafo.gesamt_gewicht_kg} onChange={v => update(i, 'gesamt_gewicht_kg', v)}/></Field>
                {isOel(trafo) && (
                  <Field label={t('Oelgewicht [kg]')} hint={t('Nur bei oel-gekuehlten Trafos')}>
                    {/* Beide Spalten gleich halten: Die Oelwannen-Bewertung liest
                        gesamt_oel_gewicht_kg, dieses Feld schrieb oel_gewicht_kg.
                        Eine Korrektur hier aenderte an der Bewertung nichts. */}
                    <NumberInput value={trafo.gesamt_oel_gewicht_kg ?? trafo.oel_gewicht_kg}
                      onChange={v => setItems(items.map((it, idx) => idx === i ? { ...it, oel_gewicht_kg: v, gesamt_oel_gewicht_kg: v } : it))}/>
                  </Field>
                )}
              </Row>
              {!trafo.trafoart && <div style={{ fontSize: 14, color: C.gray500, fontStyle: 'italic' }}>{t('Trafo-Art oben auswaehlen, um zu entscheiden ob Oelgewicht-Feld noetig ist.')}</div>}
            </Subsection>

            <Subsection title={t('Anschluesse')}>
              <Row columns={2}>
                <Field label={t('OS-Anschluss')} hint={trafo.trafoart ? undefined : t('Trafo-Art oben wählen, dann passt die Liste')}><Select value={trafo.os_anschluss} onChange={v => update(i, 'os_anschluss', v || null)} options={anschlussOptionen(OS_JE_ART, trafo.trafoart, trafo.os_anschluss)}/></Field>
                <Field label={t('US-Anschluss')}><Select value={trafo.us_anschluss} onChange={v => update(i, 'us_anschluss', v || null)} options={anschlussOptionen(US_JE_ART, trafo.trafoart, trafo.us_anschluss)}/></Field>
              </Row>
            </Subsection>

            <Subsection title={t('Schutzfunktionen')}>
              <div style={{ padding: 12, background: C.white, borderRadius: 8, marginBottom: 10 }}>
                <div style={{ fontSize: 14, fontWeight: 700, color: C.gray700, marginBottom: 8 }}>{t('Welche Schutzfunktionen sind vorhanden?')}</div>
                {istGh && (
                  <div style={{ fontSize: 14, color: C.gray500, marginBottom: 8 }}>{t('Gießharztrafo: Als Schutz gibt es nur den Kaltleiter.')}</div>
                )}
                {(!istGh || schutz.vollschutz?.vorhanden) && <div style={{ marginBottom: 8, opacity: keinSchutzActive ? 0.45 : 1 }}>
                  <Checkbox checked={schutz.vollschutz?.vorhanden} onChange={v => !keinSchutzActive && updSchutzN(i, 'vollschutz', 'vorhanden', v)} label={t('Vollschutz') + (keinSchutzActive ? ' ' + t('(gesperrt: "Kein Schutz" aktiv)') : '')}/>
                  {schutz.vollschutz?.vorhanden && !keinSchutzActive && <div style={{ marginLeft: 28, marginTop: 4 }}><Field label={t('Typ')}><TextInput value={schutz.vollschutz.typ} onChange={v => updSchutzN(i, 'vollschutz', 'typ', v)}/></Field></div>}
                </div>}
                {(!istGh || schutz.buchholz?.vorhanden) && <div style={{ marginBottom: 8, opacity: keinSchutzActive ? 0.45 : 1 }}>
                  <Checkbox checked={schutz.buchholz?.vorhanden} onChange={v => !keinSchutzActive && updSchutzN(i, 'buchholz', 'vorhanden', v)} label={t('Buchholz') + (keinSchutzActive ? ' ' + t('(gesperrt: "Kein Schutz" aktiv)') : '')}/>
                  {schutz.buchholz?.vorhanden && !keinSchutzActive && <div style={{ marginLeft: 28, marginTop: 4 }}><Field label={t('Typ')}><TextInput value={schutz.buchholz.typ} onChange={v => updSchutzN(i, 'buchholz', 'typ', v)}/></Field></div>}
                </div>}
                {(!istGh || schutz.thermometer?.vorhanden) && <div style={{ marginBottom: 8, opacity: keinSchutzActive ? 0.45 : 1 }}>
                  <Checkbox checked={schutz.thermometer?.vorhanden} onChange={v => !keinSchutzActive && updSchutzN(i, 'thermometer', 'vorhanden', v)} label={t('Thermometer') + (keinSchutzActive ? ' ' + t('(gesperrt: "Kein Schutz" aktiv)') : '')}/>
                  {schutz.thermometer?.vorhanden && !keinSchutzActive && <div style={{ marginLeft: 28, marginTop: 4 }}><Field label={t('Typ')}><TextInput value={schutz.thermometer.typ} onChange={v => updSchutzN(i, 'thermometer', 'typ', v)}/></Field></div>}
                </div>}
                {/* Oel-Trafo hat keinen Kaltleiter (Anna-Lena 18.09.2026). */}
                {(!isOel(trafo) || schutz.kaltleiter) && <div style={{ opacity: keinSchutzActive ? 0.45 : 1 }}>
                  <Checkbox checked={schutz.kaltleiter} onChange={v => !keinSchutzActive && updSchutz(i, 'kaltleiter', v)} label={t('Kaltleiter') + (keinSchutzActive ? ' ' + t('(gesperrt: "Kein Schutz" aktiv)') : '')}/>
                </div>}
                <div style={{ marginTop: 8, paddingTop: 8, borderTop: '1px dashed ' + C.gray200, opacity: otherSchutzActive ? 0.45 : 1 }}>
                  <Checkbox checked={schutz.kein_schutz} onChange={v => !otherSchutzActive && updSchutz(i, 'kein_schutz', v)} label={t('Kein Schutz vorhanden') + (otherSchutzActive ? ' ' + t('(gesperrt: andere Schutz-Option aktiv)') : '')}/>
                </div>
              </div>
              <div style={{ padding: 12, background: C.white, borderRadius: 8, marginBottom: 10 }}>
                <div style={{ fontSize: 14, fontWeight: 700, color: C.gray700, marginBottom: 8 }}>{t('Anschluss-Status')}</div>
                <Field label={t('Schutz am Trafo')}><TriToggle value={schutz.schutz_am_trafo} onChange={v => updSchutz(i, 'schutz_am_trafo', v)} optionA={t('Angeschlossen')} optionB={t('Nicht angeschlossen')}/></Field>
                <Field label={t('Schutz an MS-Anlage')}><TriToggle value={schutz.schutz_an_ms_anlage} onChange={v => updSchutz(i, 'schutz_an_ms_anlage', v)} optionA={t('Angeschlossen')} optionB={t('Nicht angeschlossen')}/></Field>
                <Field label={t('Ausloesespule an MS-Anlage')}><TriToggle value={schutz.ausloesespule_ms_anlage} onChange={v => updSchutz(i, 'ausloesespule_ms_anlage', v)} optionA={t('Vorhanden')} optionB={t('Nicht vorhanden')}/></Field>
                <Field label={t('Mitnahmeschaltung')}><Select value={schutz.mitnahmeschaltung} onChange={v => updSchutz(i, 'mitnahmeschaltung', v)} options={mitT(MITNAHME)}/></Field>
              </div>
              <Field label={t('Sonstiges')}><TextInput value={trafo.sonstiges} onChange={v => update(i, 'sonstiges', v)} placeholder={t('Zusaetzliche Anmerkungen zur Schutzfunktion')}/></Field>
            </Subsection>

            <Field label={t('Allgemeine Notizen zum Trafo')}><TextInput value={trafo.notizen} onChange={v => update(i, 'notizen', v)}/></Field>

            <NSKabelBlock trafo={trafo} onChange={(k, v) => update(i, k, v)}/>
            {einzeln ? (
              isOel(trafo) && (
                <div style={{ fontSize: 14, color: C.gray500, fontStyle: 'italic', marginTop: 8 }}>
                  {t('Die Ölauffangwanne hat im Protokoll ein eigenes Kapitel.')}
                </div>
              )
            ) : (
              <OelWanneBlock trafo={trafo} onChange={(k, v) => update(i, k, v)}/>
            )}
          </div>
        );
      })}
      {!einzeln && (
        <button onClick={add} style={{ marginTop: 4, padding: '12px 18px', background: C.navy, color: C.yellow, border: 'none', borderRadius: 8, cursor: 'pointer', fontWeight: 800, fontSize: 14 }}>{t('+ Transformator hinzufuegen')}</button>
      )}
    </>
  );
}
